import { Link } from 'react-router-dom';
import { CheckIcon, MessageCircleIcon } from 'lucide-react';
import { useCartActions } from '../../contexts/StoreContext';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { useSite } from '../../contexts/SiteContext';
import { SALON_CODE } from '../../data/codes';
import { ProductImage } from '../../ui/ProductImage';
import { longDate } from '../../core/format/dates';
import { euro } from '../../core/format/currency';
import { isSoldOut } from '../../core/rules/stock';
import { telHref } from '../../core/rules/contact';
import { shopLabels } from '../../data/shopLabels';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import type { BookingControl } from './useBooking';

/** Na het versturen: bevestiging, en het thuisprotocol dat bij de behandeling hoort. */
export function BookingDone({
  b
}: {
  b: BookingControl;
}) {
  const {
    policy
  } = useSite();
  const {
    addRoutine,
    applyCode
  } = useCartActions();
  const {
    account,
    name,
    selected,
    session,
    day,
    time,
    upsell,
    upsellTotals,
    close,
    signedIn
  } = b;
  const available = upsellTotals ? upsellTotals.items.filter(p => !isSoldOut(p)) : [];
  const availablePrice = available.reduce((s, p) => s + p.price, 0);
  return <div data-id="element-8357" data-mp="3ihugptgmy7w2">
      <div className="flex flex-col items-center text-center" data-id="element-8358" data-mp="3ihm4m3sc76lx">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blush" aria-hidden="true" data-id="element-8359" data-mp="3dkch4toid50q">
          <CheckIcon className="h-5 w-5 text-ink" strokeWidth={1.5} data-id="element-8360" data-mp="38n2iqoj55j0r" />
        </span>
        <h3 className="mt-6 font-display text-2xl text-ink" data-id="element-8361" data-mp="33lwlqeig79lf">Tot binnenkort, {account?.customer.firstName ?? name}</h3>
        <p className="mt-3 max-w-xs text-[15px] leading-relaxed text-muted" data-id="element-8362" data-mp="2zlcs3kx32o41">
          Je aanvraag voor {selected?.name ?? 'je behandeling'}
          {session ? ` (sessie ${session})` : ''} op {day && longDate(day)} om {time}
          {b.confirmedWith ? ` bij ${b.confirmedWith}` : ''} is binnen. {founder.firstName} bevestigt persoonlijk binnen{' '}
          {policy.responseHours} uur.
        </p>
        {/* Na het versturen is de voorwaarde uit de knopbalk weg; hier staat hij nog één keer. */}
        <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-muted" data-id="element-8363" data-mp="1gs2w37lf4mha">Toch niet gelegen? {policyCopy.cancel}.</p>
        {signedIn ? <Link to="/account/afspraken" onClick={close} className="mt-3 text-[14px] text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8364" data-mp="17x51lw2mh1nu">
            Bekijk het in Mijn omgeving
          </Link> : <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-muted" data-id="element-8365" data-mp="1lpkxmcx3vzf9">
            Geen bevestiging gezien? Kijk even in je spambox, of stuur een appje naar{' '}
            <a href={telHref(salon.phone)} className="text-ink underline underline-offset-4" data-id="element-8366" data-mp="1qpz6tyag1fev">
              {salon.phone}
            </a>
            .
          </p>}
      </div>

      {/* Alles uit het thuisprotocol uitverkocht? Dan niet aanbieden: de knop zou niets toevoegen. */}
      {upsell && upsellTotals && available.length > 0 && <section className="mt-8 border-t border-line pt-6" aria-labelledby="booking-upsell" data-id="element-8367" data-mp="2pbynvtmzzzv">
          <p className="text-[14px] text-clay" data-id="element-8368" data-mp="gp3hqaz0g6kq">Het resultaat vasthouden doe je thuis</p>
          <h4 id="booking-upsell" className="mt-2 font-display text-xl text-ink" data-id="element-8369" data-mp="bqtm5z9dj3yl">
            {upsell.name}
          </h4>
          <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-8370" data-mp="3k7oa88liijsm">
            Het thuisprotocol dat {founder.firstName} bij deze behandeling meegeeft, inclusief een huidplan op papier. Na je behandeling werkt ze het persoonlijk bij.
          </p>
          <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-8371" data-mp="3p680umb1wjrb">
            {upsellTotals.items.map(p => <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-8372" data-mp="31fltkx8vf3id">
                {/* Decoratief: de naam staat ernaast. */}
                <ProductImage src={p.image} alt="" className="h-12 w-10 shrink-0 p-1" data-id="element-8373" data-mp="2wgrarvawley9" />
                <span className="flex-1 text-[14px] leading-snug text-ink" data-id="element-8374" data-mp="3a5c8bxwd0y95">
                  {p.name}
                  {isSoldOut(p) && <span className="block text-[12px] text-clay" data-id="element-8375" data-mp="3f3dreitscxkm">{shopLabels.soldOutShort}</span>}
                </span>
                <span className={`text-[14px] ${isSoldOut(p) ? 'text-muted line-through decoration-line' : 'text-muted'}`} data-id="element-8376" data-mp="3k0cjywxswwgp">{euro(p.price)}</span>
              </li>)}
          </ul>
          <button type="button" onClick={() => {
        addRoutine(upsell.id);
        applyCode(SALON_CODE);
        close();
      }} className={`${btnPrimary} mt-5 w-full`} data-id="element-8377" data-mp="3swlnamowgf84">
            {/* De prijs van wat er echt in de mand gaat: uitverkochte producten tellen niet mee. */}
            Leg het thuisprotocol klaar · {euro(availablePrice)}
          </button>
          <p className="mt-2 text-center text-[13px] text-muted" data-id="element-8378" data-mp="2dd8o2zc2jwfq">Of laat het klaarstaan in de salon en betaal na je behandeling.</p>
        </section>}

      <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnSecondary} mt-8 w-full`} data-id="element-8379" data-mp="ujxi2z6s392p">
        <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8380" data-mp="ppt9kbhv6szi" />
        Iets doorgeven via WhatsApp
        <span className="sr-only" data-id="element-8381" data-mp="grhiy9gzzv72"> {shopLabels.newTab}</span>
      </a>
    </div>;
}